<template>
    <div id="searchResults" class="search-results-container" :class="{'quick-search': searchType === 'Smart'}">
        <div class="feature-area-wrapper">
            <div class="feature-area feature-area-compact">
                <searchResultsMenu :countLoader="countLoader" />
                <navigationTabs />
            </div>
        </div>

        <div class="searchResults">
            <div class="adv-results-container" ref="resultsContainer">
                <facets class=""  ref="facets"/>
                <div class="content-with-sidebar" >
                    <div class="field-error-message error-large" v-if="serviceError">
                        Unable to load publication data
                        <div class="error-code">
                        {{serviceError}}
                        </div>
                    </div>
                    <div v-if="!publicationResults && publicationTable.isLoading" class="loading"><div class="loader-bg"></div><span class="loader">Loading...</span></div>
                    <pubDataTable class="dataTable"
                        :table="publicationTable"
                        fromPage="publications"
                        ref="dataTable"
                        v-if="publicationResults" />
                </div>
            </div>
        </div>

        <!-- Export -->
        <exportModule ref="exportModule" fromPage="publications" />
    </div>
</template>

<script>
// @ is an alias to /src
import { mapGetters, mapActions } from 'vuex';
import pubDataTable from '@/components/global/pubDataTable.vue';
import navigationTabs from '@/components/global/navigationTabs.vue';
import Charts from '@/components/global/Charts.vue';
import facets from '@/components/global/facets.vue';
import searchResultsMenu from '@/components/global/searchResultsMenu.vue';
import globalMixins from '@/globalMixins';
import projectListMixins from '@/projectListMixins';
import exportModule from  '@/components/global/export.vue';
import { isProxy, toRaw  } from 'vue';
import bus from '@/shared/event-bus.js';

export default {
    name: 'publications',
    mixins: [globalMixins, projectListMixins],
    components: {
      pubDataTable,
      navigationTabs,
      facets,
      Charts,
      searchResultsMenu,
      exportModule
    },
    computed: {
        ...mapGetters([
            'searchCriteria',
            'editSearchCriteria',
            'searchMeta',
            'publicationResults',
            'publicationsFacetData',
            'coreProjCountPub'
        ])
    },
    watch: {
      publicationResults: function () {
        let pubData = this.publicationResults;
        if (isProxy(this.publicationResults)){
            pubData = toRaw(this.publicationResults)
        }

        if (pubData === null) {
          this.setCoreProjCountPub(null);
        } else if (pubData !== null) {
          if (!this.publicationsFacetData) {
            this.$refs.facets.getPubFacets();
          } else if (!this.coreProjCountPub) {
            this.getCoreProjectsCount();
          }
        }
      },
      publicationsFacetData: function () {
        if (this.publicationsFacetData === null) {
          this.setCoreProjCountPub(null);
        } else if (this.publicationsFacetData !== null && !this.coreProjCountPub) {
          this.getCoreProjectsCount();
        }
      }
    },
    data: function () {
        return {
            serverless: false,
            data: null,
            serviceError: null,
            countLoader: false
        };
    },
    mounted () {
        var loadData = false;

        if ((this.searchMeta && !this.searchMeta.search_id) || !this.$route.params.search_id) {
            this.$router.push({ path: '/' }).catch(err => {});
        } else {
           if (!sessionStorage.getItem('publicationPageLoaded')) {
                window.sessionStorage.setItem('publicationPageLoaded', 'true');
                this.logPublicationsPage();
            }
            this.showCriteria = false;
            if (this.$store.state.searchFacetCriteria && this.$store.state.searchFacetCriteria['publication_years'] && this.$store.state.searchFacetCriteria['publication_years'].length > 0) {
                loadData = true;
                this.$store.state.searchFacetCriteria['publication_years'] = [];
            }
            if (!this.publicationResults || loadData) {
                this.publicationTable.isLoading = true;
                if (this.$route.query.sort_field) {
                    this.publicationTable.sortField = this.$route.query.sort_field;
                }
                if (this.$route.query.sort_order) {
                    this.publicationTable.sortOrder = this.$route.query.sort_order;
                }
                if (this.$route.query) {
                    this.parseFacetFiltersFromURL();
                }
                this.publicationTable.columns.coreProjectNumber.isVisible = true;
                this.getPublicationResults(false);
            } else {
                if (this.publicationResults.meta.sort_field) {
                    this.publicationTable.sortField = this.publicationResults.meta.sort_field;
                    this.publicationTable.sortOrder = this.publicationResults.meta.sort_order;
                    let facetToQueryStr = this.$refs.facets.generateQueryString();

                    facetToQueryStr['sort_field'] = this.publicationTable.sortField;
                    facetToQueryStr['sort_order'] = this.publicationTable.sortOrder;
                    this.$router.push({ query: facetToQueryStr}).catch(err => {});
                }
            }
            bus.$off('get-applId');
            bus.$on('get-applId', (data, callback) => {
                this.getApplId(data, callback)
            });
            bus.$off('sort-column');
            bus.$on('sort-column', ($e1, data) => {
                this.setSortDetails($e1, data)
            });
        }

        this.setFullscreen(true);
        setTimeout(() => {
            bus.$emit('update:resize');
        }, 1);
    },
    methods: {
        ...mapActions([
            'setFullscreen',
            'setEditSearchResultsFlag',
            'setLookup',
            'setSearchMode',
            'setPublicationResults',
            'setCoreProjCountPub'
        ]),
      getCoreProjectsCount() {
            var parsedCriteria = window.parseCriteria(this.searchCriteria, {isSubmitForm: false}),
                criteria = window.stringifySearchCriteriaFields(parsedCriteria),
                searchParam = {
                    offset: this.publicationTable.offset,
                    limit: this.publicationTable.limit,
                    facet_filters: this.searchFacetCriteria,
                    criteria: criteria,
                    is_shared: this.$route.query['shared'] ? true : false,
                    CoreProjSortField: 'fiscal_year', // hardcoded to fetch top 10000 publications
                    CoreProjSortOrder: 'desc',
                    cache_id: this.publicationResults.meta.properties.CacheId
                },
                callback = (data) => {
                    this.setCoreProjCountPub(data.results[0]);
                };

            if (this.$route.params.search_id) {
                searchParam['search_id'] = this.$route.params.search_id;
            }

            window.fetchPublicationCoreProjCount(searchParam, callback);
        },
        setSortDetails ($event, selectedSortColumn) {
            this.isLoading = true;
            this.setPublicationSortDetails(selectedSortColumn, 'publicationTable', this.getPublicationResults);
        },
        setProjectPageNumber (requestedPage) {
            if (requestedPage === this.$options.name) {
                if (this.$store.state.publicationResults && this.$store.state.publicationResults.meta.total > this.publicationTable.offset + this.publicationTable.limit) {
                    this.publicationTable.loadingMoreData = true;
                    this.publicationTable.offset += this.publicationTable.limit;
                    this.getPublicationResults(false);
                }
            }
            // this.setPublicationPageNumber(fieldName);
        },
        exportData (selectedFieldsToExport, callback) {
            var searchParam,
                criteria = copyData(this.$refs.facets.searchFacetCriteria);

            if (!criteria['publication_years'] || (criteria['publication_years'] && criteria['publication_years'].length === 0)) {
                if (this.$refs.facets && this.$refs.facets.publicationsFacetData && this.$refs.facets.publicationsFacetData.facets) {
                    criteria['publication_years'] = this.$refs.facets.publicationsFacetData.facets[0].values.map((item) => {
                        return item.value;
                    });
                }
            }

            searchParam = {
                "search_id": this.$route.params.search_id,
                "facet_filters": criteria,
                "file_format": this.$refs.exportModule.exportFileFormat,
                "offset": 0,
                "limit": 0,
                "sort_field": this.publicationTable.sortField,
                "sort_order": this.publicationTable.sortOrder,
                "cache_id": this.publicationResults.meta.properties.CacheId
            }
            this.getExportFile(searchParam, callback);
        },
        scrollToResultsTop () {
            let resultsOffsetTop = this.getResultsContainerOffsetTop();

            // only scroll up if user view is scrolled below top of table
            if (window.pageYOffset > resultsOffsetTop) {
                window.scrollTo(0, this.getResultsContainerOffsetTop());
            }
        },
        getResultsContainerOffsetTop () {
            return this.$refs.resultsContainer ? this.$refs.resultsContainer.offsetTop : 0;
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';
.title-container {
    position: relative;
    min-height: 70px;
    .loader {
        top: 6px;
        height: 7em;
        width: 7em;
        left: 5%;
    }
}
</style>
